import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FieldPath,
  FormProvider,
  SubmitErrorHandler,
  SubmitHandler,
  useFieldArray,
  useForm,
  useFormContext,
  useWatch,
} from 'react-hook-form';

import { Accordion } from '@radix-ui/react-accordion';
import {
  BoxIcon,
  Building2,
  CircleDollarSignIcon,
  CircleHelp,
  MapPinIcon,
  MapPinnedIcon,
  PackageIcon,
  ReceiptIcon,
  TruckIcon,
  WarehouseIcon,
  Weight,
} from 'lucide-react';

import { Button } from 'components/Button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/Tooltip';
import { FormatPhoneNumber, RHFTextInput } from 'components/input/RHFTextInput';
import { Flex } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { ExtendedFormProvider } from 'contexts/extendedFormContext';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useGuardedCreateLoad } from 'hooks/useGuardedCreateLoad';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { useToast } from 'hooks/useToaster';
import { getCustomers } from 'lib/api/getCustomers';
import { getLocations } from 'lib/api/getLocations';
import { searchCustomers } from 'lib/api/searchCustomers';
import { updateTMS } from 'lib/api/updateTMS';
import { FormStorageService } from 'lib/services/FormStorage/service';
import { LoadSectionAccordionItem } from 'pages/LoadView/LoadInformation/Components';
import { useEnsureTmsCustomerRowForExternalId } from 'pages/QuoteView/LoadBuilding/AljexSectionForms/hooks/useEnsureTmsCustomerRowForExternalId';
import LoadBuildingAccordionControls from 'pages/QuoteView/LoadBuilding/components/LoadBuildingAccordionControls';
import { LoadCreatedMessage } from 'pages/QuoteView/LoadBuilding/components/LoadCreatedMessage';
import { useLoadBuildingAccordionTabs } from 'pages/QuoteView/LoadBuilding/hooks/useLoadBuildingAccordionTabs';
import { suggestedTransportTypeOrEmpty } from 'pages/QuoteView/LoadBuilding/utils/suggestedEquipment';
import {
  AdditionalReference,
  Commodity,
  CompanyCoreInfo,
  CostLineItem,
  Customer,
  Dropoff,
  Load,
  LoadCarrier,
  LoadStopsItem,
  LoadStopsItemTypes,
  NormalizedDropoff,
  NormalizedLoad,
  NormalizedPickup,
  Note,
  Pickup,
  RateData,
  TMSCustomer,
  TMSLocation,
  createInitLoad,
  createInitRateData,
  createInitSpecs,
  normalizeLoad,
} from 'types/Load';
import { Maybe, MaybeUndef } from 'types/UtilityTypes';
import { TMSOperation } from 'types/api/UpdateTMS';
import ButtonText from 'types/enums/ButtonText';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { TMS } from 'types/enums/Integrations';
import { SuggestionPipelines } from 'types/suggestions/CoreSuggestions';
import {
  LoadBuildingSuggestions,
  SuggestedLoad,
} from 'types/suggestions/LoadBuildingSuggestions';
import {
  acceptLoadViewSuggestion,
  useApplyLoadViewSuggestion,
  useFocusLoadViewSuggestion,
} from 'utils/applyLoadViewSuggestion';
import { applyCompleteKitSuggestions } from 'utils/completeKitSuggestion';
import {
  DEFAULT_MAX_VALIDATION_TOAST_LABELS,
  ManualFieldError,
  applyManualValidationErrors,
  handleFormErrors,
  loadBuildingManualValidationDisplayOptions,
  scheduleLoadBuildingSuggestionValidation,
} from 'utils/formValidation';
import {
  injectSelectedObject,
  normalizeToArray,
  normalizeToStringArray,
  withLoadBuildingSuggestionContext,
} from 'utils/loadInfoAndBuilding';
import { denormalizeDatesForTMSForm } from 'utils/parseDatesForTMSForm';

import {
  validateDeliveryOnOrAfterPickupDate,
  validateDuplicateStopLocations,
} from '../utils/stopValidation';
import AdditionalReferencesForm from './AdditionalReferences';
import BillToSectionForm from './BillTo';
import CarrierSectionForm from './Carrier';
import CustomerSectionForm from './Customer';
import RatesForm, {
  getFuelFlatDisplayValue,
  withUpdatedFuelItem,
} from './Rates';
import SpecificationsForm from './Specifications';
import StopForm from './Stop';
import { getAljexCharacterLimit } from './characterLimits';
import {
  mapAljexRateTypeToCode,
  normalizeAljexRateType,
  normalizeAljexTransportType,
} from './utils';

const ALJEX_STOP_LOCATION_TOOLTIP =
  'Selecting a saved location is optional. You can build the load using the address fields below.';

function AljexStopSectionInfoTooltip() {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className='inline-flex shrink-0 cursor-help rounded-sm text-neutral-500 hover:text-neutral-700'
            aria-label='About pickup and delivery locations'
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <CircleHelp className='h-4 w-4' aria-hidden />
          </span>
        </TooltipTrigger>
        <TooltipContent side='top' className='max-w-[16rem] text-left'>
          {ALJEX_STOP_LOCATION_TOOLTIP}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

enum AvailableTabs {
  customer = 'customer',
  billTo = 'billTo',
  equipmentType = 'equipmentType',
  commodities = 'commodities',
  route = 'route',
  pickup = 'pickup',
  consignee = 'consignee',
  rates = 'rates',
  additionalReferences = 'additionalReferences',
  carrier = 'carrier',
}

type LoadBuildingTextInputProps = React.ComponentPropsWithoutRef<
  typeof RHFTextInput
> & { name: FieldPath<NormalizedLoad> };

export const LoadBuildingTextInput = (props: LoadBuildingTextInputProps) => {
  const { name } = props;
  const maxLength = getAljexCharacterLimit(name);
  const { setValue } = useFormContext<NormalizedLoad>();
  const currentValue = useWatch({ name });

  // Truncate value if it exceeds maxLength when component mounts or value changes
  useEffect(() => {
    if (
      maxLength &&
      typeof currentValue === 'string' &&
      currentValue.length > maxLength
    ) {
      setValue(name, currentValue.substring(0, maxLength), {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [currentValue, maxLength, name, setValue]);

  return <RHFTextInput {...props} maxLength={maxLength} />;
};

// Initialize and normalize additional refs to prevent highlighting empty refs as dirty fields
const normalizeAdditionalReferences = (
  existingRefs: AdditionalReference[] = []
): AdditionalReference[] => {
  const defaultRefs = [
    {
      qualifier: 'BL',
      number: '',
      weight: 0,
      pieces: 0,
      shouldSendToDriver: false,
    },
    {
      qualifier: 'LOAD',
      number: '',
      weight: 0,
      pieces: 0,
      shouldSendToDriver: false,
    },
    // 6 generic references, hidden by default
    ...Array.from({ length: 6 }, () => ({
      qualifier: '',
      number: '',
      weight: 0,
      pieces: 0,
      shouldSendToDriver: false,
    })),
  ];

  // If we have existing additional references, merge them into the default structure
  if (existingRefs && existingRefs.length > 0) {
    existingRefs.forEach((ref: AdditionalReference) => {
      if (ref.qualifier === 'BL' || ref.qualifier === 'BOL') {
        defaultRefs[0] = {
          qualifier: ref.qualifier,
          number: ref.number,
          weight: ref.weight,
          pieces: ref.pieces,
          shouldSendToDriver: ref.shouldSendToDriver,
        };
      } else if (ref.qualifier === 'LOAD') {
        defaultRefs[1] = {
          qualifier: ref.qualifier,
          number: ref.number,
          weight: ref.weight,
          pieces: ref.pieces,
          shouldSendToDriver: ref.shouldSendToDriver,
        };
      } else {
        // For generic references, find the first available slot
        const availableIndex = defaultRefs.findIndex(
          (dr, index) => index >= 2 && dr.number === ''
        );
        if (availableIndex !== -1) {
          defaultRefs[availableIndex] = {
            qualifier: ref.qualifier,
            number: ref.number,
            weight: ref.weight,
            pieces: ref.pieces,
            shouldSendToDriver: ref.shouldSendToDriver,
          };
        }
      }
    });
  }

  return defaultRefs;
};

const buildStopsFromPickupAndConsignee = (
  pickup: MaybeUndef<Pickup | NormalizedPickup>,
  consignee: MaybeUndef<Dropoff | NormalizedDropoff>
): LoadStopsItem[] => {
  const buildStopWithNestedAddress = (
    index: number,
    stopData: Pickup | NormalizedPickup | Dropoff | NormalizedDropoff,
    stopType: LoadStopsItemTypes
  ): LoadStopsItem => {
    const toDateString = (value: any): string => {
      if (!value) return '';
      if (value instanceof Date) return value.toISOString();
      return String(value);
    };

    return {
      loadID: 0,
      stopType,
      order: index,
      stopNumber: index,
      externalTMSID: stopData.externalTMSID ?? '',
      contact: stopData.contact ?? '',
      phone: stopData.phone ?? '',
      email: stopData.email ?? '',
      businessHours: stopData.businessHours ?? '',
      refNumber: stopData.refNumber ?? '',
      readyTime:
        'readyTime' in stopData ? toDateString(stopData.readyTime) : '',
      readyEndTime: '',
      mustDeliver:
        'mustDeliver' in stopData ? toDateString(stopData.mustDeliver) : '',
      apptRequired: stopData.apptRequired ?? false,
      apptType: '',
      apptStartTime: toDateString(stopData.apptStartTime),
      apptEndTime: toDateString(stopData.apptEndTime),
      actualStartTime: '',
      actualEndTime: '',
      expectedStartTime: '',
      apptNote: stopData.apptNote ?? '',
      timezone: stopData.timezone ?? '',
      additionalReferences: null,
      address: {
        name: stopData.name ?? '',
        addressLine1: stopData.addressLine1 ?? '',
        addressLine2: stopData.addressLine2 ?? '',
        city: stopData.city ?? '',
        state: stopData.state ?? '',
        zip: stopData.zipCode ?? '',
        country: stopData.country ?? '',
        timezone: stopData.timezone ?? '',
        isVerified: false,
      },
    };
  };

  const defaultStops = createInitLoad().stops!;

  const pickupStop = pickup
    ? buildStopWithNestedAddress(0, pickup, LoadStopsItemTypes.Pickup)
    : defaultStops[0];

  const consigneeStop = consignee
    ? buildStopWithNestedAddress(1, consignee, LoadStopsItemTypes.Dropoff)
    : defaultStops[1];

  return [pickupStop, consigneeStop] as LoadStopsItem[];
};

const serializePickupData = (
  location: MaybeUndef<Pickup | NormalizedPickup>
): MaybeUndef<Pickup> => {
  if (!location) return location;

  return {
    ...location,
    phone: location.phone ? FormatPhoneNumber(location.phone) : location.phone,
    apptStartTime: location.apptStartTime
      ? new Date(location.apptStartTime).toISOString()
      : null,
    apptEndTime: location.apptEndTime
      ? new Date(location.apptEndTime).toISOString()
      : null,
    readyTime: location.readyTime
      ? new Date(location.readyTime).toISOString()
      : null,
    refNumberCandidates: normalizeToStringArray(location.refNumberCandidates),
  } as Pickup;
};

const serializeDropoffData = (
  location: MaybeUndef<Dropoff | NormalizedDropoff>
): MaybeUndef<Dropoff> => {
  if (!location) return location;

  return {
    ...location,
    phone: location.phone ? FormatPhoneNumber(location.phone) : location.phone,
    apptStartTime: location.apptStartTime
      ? new Date(location.apptStartTime).toISOString()
      : null,
    apptEndTime: location.apptEndTime
      ? new Date(location.apptEndTime).toISOString()
      : null,
    mustDeliver: location.mustDeliver
      ? new Date(location.mustDeliver).toISOString()
      : null,
    refNumberCandidates: normalizeToStringArray(location.refNumberCandidates),
  } as Dropoff;
};

const initLoadBuildingForm = (tmsName: string): NormalizedLoad => {
  return normalizeLoad(tmsName, {
    ...createInitLoad(),
    mode: 'Truckload',
    specifications: {
      ...createInitSpecs(),
      transportType: 'V',
    },
    rateData: {
      ...createInitRateData(),
      customerRateType: 'Flat Rate',
      carrierRateType: 'Flat Rate',
    },
    additionalReferences: normalizeAdditionalReferences([]),
  });
};

interface UnifiedAljexLoadFormProps {
  isCreateMode: boolean;
  load?: Load;
  onSubmit?: (data: NormalizedLoad) => void;
  onSuccess?: (result: any) => void;
  onError?: (error: any) => void;
}

const isLoadBuildingSuggestion = (
  suggestion: Maybe<{ pipeline: SuggestionPipelines }>
): suggestion is LoadBuildingSuggestions =>
  suggestion?.pipeline === SuggestionPipelines.LoadBuilding;

export default function UnifiedAljexLoadForm({
  isCreateMode,
  load,
  onSubmit,
  onSuccess,
  onError,
}: UnifiedAljexLoadFormProps) {
  const tmsName = TMS.Aljex;
  const { tmsIntegrations } = useServiceFeatures();
  const tmsID = isCreateMode ? tmsIntegrations[0]?.id : load?.tmsID;

  const { toast } = useToast();
  const guardedCreateLoad = useGuardedCreateLoad();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadBuildingSuggestionClicked, setIsLoadBuildingSuggestionClicked] =
    useState(false);
  const [showAIHints, setShowAIHints] = useState(false);

  const [isLoadingCustomers, setIsLoadingCustomers] = useState(true);
  const [isLoadingLocations, setIsLoadingLocations] = useState(true);
  const [customers, setCustomers] = useState<Maybe<TMSCustomer[]>>(null);
  const [locations, setLocations] = useState<Maybe<TMSLocation[]>>(null);
  const [builtLoad, setBuiltLoad] = useState<Maybe<Load>>(null);

  const {
    currentState: {
      clickedSuggestion,
      threadId,
      emailId,
      threadItemId,
      goToSuggestion,
    },
    setCurrentState,
  } = useContext(SidebarStateContext);

  const fetchCustomers = async () => {
    if (!tmsID) return;

    setIsLoadingCustomers(true);

    const res = await getCustomers(tmsID);
    if (res.isOk()) {
      setCustomers(res.value.customerList);
    } else {
      toast({
        description: res.error.message || 'Error while fetching customer list.',
        variant: 'destructive',
      });
    }
    setIsLoadingCustomers(false);
  };

  const fetchLocations = async (forceRefresh = false) => {
    if (!tmsID) return;

    setIsLoadingLocations(true);

    const res = await getLocations(tmsID, forceRefresh);
    if (res.isOk()) {
      setLocations(res.value.locationList);
      if (forceRefresh) {
        toast({
          description: 'Successfully refreshed location list.',
          variant: 'success',
        });
      }
    } else {
      const errorMessage = forceRefresh
        ? 'Error while refreshing location list.'
        : 'Error while fetching location list.';
      toast({
        description: res.error.message || errorMessage,
        variant: 'destructive',
      });
    }
    setIsLoadingLocations(false);
  };

  const handleRefreshLocations = async () => {
    await fetchLocations(true);
  };

  // Latest-ref bootstrap: this effect must run only when the TMS integration
  // changes, but the callbacks it invokes are re-created every render.
  const bootstrapTmsDataRef = useRef<() => void>(() => {});
  bootstrapTmsDataRef.current = () => {
    if (
      isCreateMode &&
      (!clickedSuggestion ||
        clickedSuggestion.pipeline !== SuggestionPipelines.LoadBuilding)
    ) {
      goToSuggestion({
        suggestionPipeline: SuggestionPipelines.LoadBuilding,
      });
    }
    void fetchCustomers();
    void fetchLocations();
  };

  useEffect(() => {
    bootstrapTmsDataRef.current();
  }, [tmsID]);

  useEffect(() => {
    if (
      clickedSuggestion &&
      clickedSuggestion.pipeline === SuggestionPipelines.LoadBuilding
    ) {
      setIsLoadBuildingSuggestionClicked(true);
      setShowAIHints(true);
    } else {
      setIsLoadBuildingSuggestionClicked(false);
      setShowAIHints(false);
    }
  }, [clickedSuggestion]);

  // Only clear the "Load Created" banner when switching suggestions;
  // keep it when the same suggestion is marked Accepted after building
  useEffect(() => {
    setBuiltLoad(null);
  }, [clickedSuggestion?.id]);

  const createModeSuggestionId = isCreateMode
    ? (clickedSuggestion?.id ?? null)
    : null;

  // Read the suggestion payload through a ref so the defaults below are only
  // re-derived when the create-mode suggestion id changes, never on the
  // transient clickedSuggestion identity churn from extension messages.
  const clickedSuggestionRef = useRef(clickedSuggestion);
  clickedSuggestionRef.current = clickedSuggestion;

  const memoizedDefaultValues: NormalizedLoad = useMemo(() => {
    // Edit mode: use existing load data but ensure additionalReferences has exactly 8 items
    if (!isCreateMode && load) {
      const normalizedLoad = normalizeLoad(tmsName, load);

      let stops = normalizedLoad.stops;
      if (!stops || stops.length === 0) {
        stops = buildStopsFromPickupAndConsignee(
          serializePickupData(load.pickup),
          serializeDropoffData(load.consignee)
        );
      }

      return {
        ...normalizedLoad,
        stops,
        specifications: {
          ...normalizedLoad.specifications,
          transportType: normalizeAljexTransportType(
            normalizedLoad.specifications?.transportType
          ),
        },
        rateData: {
          ...normalizedLoad.rateData,
          customerRateType: normalizeAljexRateType(
            normalizedLoad.rateData?.customerRateType
          ),
          carrierRateType: normalizeAljexRateType(
            normalizedLoad.rateData?.carrierRateType
          ),
        },
        additionalReferences: normalizeAdditionalReferences(
          normalizedLoad.additionalReferences
        ),
      };
    }

    // Create mode: check for suggestions or use init data
    if (isCreateMode) {
      if (builtLoad) {
        const normalizedBuiltLoad = normalizeLoad(tmsName, builtLoad);
        return {
          ...normalizedBuiltLoad,
          rateData: {
            ...normalizedBuiltLoad.rateData,
            customerRateType: normalizeAljexRateType(
              normalizedBuiltLoad.rateData?.customerRateType
            ),
            carrierRateType: normalizeAljexRateType(
              normalizedBuiltLoad.rateData?.carrierRateType
            ),
          },
          additionalReferences: normalizeAdditionalReferences(
            normalizedBuiltLoad.additionalReferences
          ),
        };
      }

      const activeSuggestion =
        createModeSuggestionId == null ? null : clickedSuggestionRef.current;
      if (
        activeSuggestion &&
        activeSuggestion.pipeline === SuggestionPipelines.LoadBuilding
      ) {
        const casted = activeSuggestion.suggested as SuggestedLoad;

        // convert object-with-numeric-keys back to arrays (Aljex API response issue)
        // Only convert if it's an object that's not already an array
        const commoditiesArray: Commodity[] =
          casted.commodities &&
          typeof casted.commodities === 'object' &&
          !Array.isArray(casted.commodities)
            ? Object.values(casted.commodities)
            : casted.commodities || [];

        const additionalReferencesArray: AdditionalReference[] =
          casted.additionalReferences &&
          typeof casted.additionalReferences === 'object' &&
          !Array.isArray(casted.additionalReferences)
            ? Object.values(casted.additionalReferences)
            : casted.additionalReferences || [];

        const customerLineItemsArray: CostLineItem[] =
          normalizeToArray(casted.rateData?.customerLineItems) || [];

        const carrierLineItemsArray: CostLineItem[] =
          normalizeToArray(casted.rateData?.carrierLineItems) || [];

        // Exclude customerRateNumUnits and carrierRateNumUnits from AI suggestion
        const {
          customerRateNumUnits: _omitCustomerRateNumUnits,
          carrierRateNumUnits: _omitCarrierRateNumUnits,
          ...filteredRateData
        } = casted.rateData || {};

        const customer: Customer = casted.customer
          ? {
              ...casted.customer,
              phone: casted.customer.phone
                ? FormatPhoneNumber(casted.customer.phone)
                : casted.customer.phone,
            }
          : casted.customer;
        const hasMeaningfulBillToData = !!(
          casted.billTo &&
          (casted.billTo.externalTMSID ||
            casted.billTo.name ||
            casted.billTo.addressLine1 ||
            casted.billTo.city ||
            casted.billTo.state ||
            casted.billTo.zipCode)
        );
        const formattedBillTo =
          hasMeaningfulBillToData && casted.billTo
            ? {
                ...casted.billTo,
                phone: casted.billTo.phone
                  ? FormatPhoneNumber(casted.billTo.phone)
                  : casted.billTo.phone,
              }
            : undefined;

        const serializedPickup = serializePickupData(casted.pickup);
        const serializedConsignee = serializeDropoffData(casted.consignee);

        const suggestedFields: Load = {
          ...createInitLoad(),
          ...casted,
          mode: 'Truckload',

          customer: casted.customer
            ? {
                ...casted.customer,
                phone: casted.customer.phone
                  ? FormatPhoneNumber(casted.customer.phone)
                  : casted.customer.phone,
                refNumberCandidates: normalizeToStringArray(
                  casted.customer.refNumberCandidates
                ),
              }
            : casted.customer,
          billTo: formattedBillTo ?? customer,
          stops:
            casted?.stops && casted.stops.length >= 2
              ? casted.stops
              : buildStopsFromPickupAndConsignee(
                  serializedPickup,
                  serializedConsignee
                ),
          pickup: serializedPickup as Pickup,
          consignee: serializedConsignee as Dropoff,
          specifications: {
            ...createInitSpecs(),
            ...casted.specifications,
            transportType: normalizeAljexTransportType(
              suggestedTransportTypeOrEmpty(casted.specifications)
            ),
          },
          rateData: {
            ...createInitRateData(),
            ...filteredRateData,
            customerRateType: normalizeAljexRateType(
              filteredRateData.customerRateType
            ),
            carrierRateType: normalizeAljexRateType(
              filteredRateData.carrierRateType
            ),
            customerLineItems: customerLineItemsArray,
            carrierLineItems: carrierLineItemsArray,
          },
          additionalReferences: normalizeAdditionalReferences(
            additionalReferencesArray
          ),
          commodities: commoditiesArray,
        };

        const normalizedSuggestion = normalizeLoad(tmsName, suggestedFields);
        return {
          ...normalizeLoad(tmsName, createInitLoad()),
          ...normalizedSuggestion,
          mode: 'Truckload',
          specifications: {
            ...normalizedSuggestion.specifications,
            // normalizeLoad fills '' with 'Truckload'; an email with no
            // equipment stays empty so the rep picks one.
            transportType: suggestedFields.specifications.transportType,
          },
        } as NormalizedLoad;
      }

      return initLoadBuildingForm(tmsName);
    }

    return initLoadBuildingForm(tmsName);
  }, [createModeSuggestionId, tmsName, builtLoad, isCreateMode, load]);

  const formMethods = useForm<NormalizedLoad>({
    defaultValues: memoizedDefaultValues,
  });

  const { control, handleSubmit, getValues } = formMethods;
  const clickedLoadBuildingSuggestion = isLoadBuildingSuggestion(
    clickedSuggestion
  )
    ? clickedSuggestion
    : null;
  const clickedLoadBuildingSuggestionId = clickedLoadBuildingSuggestion?.id;
  const isClickedSuggestionBillToDistinct =
    !!clickedLoadBuildingSuggestion?.isBillToDistinct;
  const defaultCustomer = memoizedDefaultValues?.customer;
  const defaultBillTo = memoizedDefaultValues?.billTo;
  const defaultPickup = memoizedDefaultValues?.pickup;
  const defaultConsignee = memoizedDefaultValues?.consignee;
  const defaultBillToExternalTMSID = defaultBillTo?.externalTMSID;
  const defaultCustomerExternalTMSID = defaultCustomer?.externalTMSID;
  const [
    shouldPreserveDistinctBillToFromSuggestion,
    setShouldPreserveDistinctBillToFromSuggestion,
  ] = useState(false);
  useEffect(() => {
    if (!isCreateMode) {
      setShouldPreserveDistinctBillToFromSuggestion(false);
      return;
    }
    if (!clickedLoadBuildingSuggestionId) {
      setShouldPreserveDistinctBillToFromSuggestion(false);
      return;
    }

    const hasDistinctBillToFromSuggestion =
      isClickedSuggestionBillToDistinct &&
      !!defaultBillToExternalTMSID &&
      defaultBillToExternalTMSID !== defaultCustomerExternalTMSID;

    // Latch this flag once a load-building suggestion with distinct bill-to is active.
    // We intentionally avoid resetting on transient clickedSuggestion clears triggered by
    // extension messages, so Bill To doesn't get overwritten mid-edit.
    if (hasDistinctBillToFromSuggestion) {
      setShouldPreserveDistinctBillToFromSuggestion(true);
      return;
    }

    setShouldPreserveDistinctBillToFromSuggestion(false);
  }, [
    isCreateMode,
    clickedLoadBuildingSuggestionId,
    isClickedSuggestionBillToDistinct,
    defaultBillToExternalTMSID,
    defaultCustomerExternalTMSID,
  ]);
  const watchedCustomerExternalTMSID = useWatch({
    control,
    name: 'customer.externalTMSID',
  });
  const watchedBillToExternalTMSID = useWatch({
    control,
    name: 'billTo.externalTMSID',
  });
  const searchCustomersNonDestructive = useCallback(
    async (field: 'name' | 'externalTMSID', value: string) => {
      if (!tmsID) return [];
      const res = await searchCustomers(tmsID, field, value);
      return res.isOk() ? (res.value.customerList ?? []) : [];
    },
    [tmsID]
  );

  const { fields } = useFieldArray<NormalizedLoad, 'stops'>({
    control,
    name: 'stops',
  });

  // Include dynamic intermediate Route stops (`stop-${index}`) so Expand all opens them.
  const allTabs = useMemo(() => {
    const intermediateStopTabs = fields
      .slice(1, -1)
      .map((_, index) => `stop-${index}`);

    const tabs = [
      AvailableTabs.customer,
      AvailableTabs.billTo,
      AvailableTabs.commodities,
      AvailableTabs.rates,
      AvailableTabs.route,
      AvailableTabs.pickup,
      ...intermediateStopTabs,
      AvailableTabs.consignee,
      AvailableTabs.additionalReferences,
    ];
    if (!isCreateMode) {
      tabs.splice(tabs.length - 1, 0, AvailableTabs.carrier);
    }
    return tabs;
  }, [fields, isCreateMode]);

  // Top-level headers only — nested Route stops can remain in activeTabs after Route closes.
  const sectionTabs = useMemo(
    () =>
      allTabs.filter(
        (tab) =>
          tab !== AvailableTabs.pickup &&
          tab !== AvailableTabs.consignee &&
          !tab.startsWith('stop-')
      ),
    [allTabs]
  );

  const {
    activeTabs,
    setActiveTabs,
    expandAll,
    collapseAll,
    defaultView,
    setDefaultView,
  } = useLoadBuildingAccordionTabs({ allTabs });

  const handleAccordionValueChange = useCallback(
    (next: string[]) => {
      let tabs = next;
      if (!tabs.includes(AvailableTabs.route)) {
        tabs = tabs.filter(
          (tab) =>
            tab !== AvailableTabs.pickup &&
            tab !== AvailableTabs.consignee &&
            !tab.startsWith('stop-')
        );
      }
      setActiveTabs(tabs);
    },
    [setActiveTabs]
  );

  // Create-mode-only Load Building suggestion id. Edit-mode Load View suggestion
  // clicks must not retrigger the reset below, or in-progress edits get wiped.
  const createModeLoadBuildingSuggestionId =
    isCreateMode &&
    clickedSuggestion?.pipeline === SuggestionPipelines.LoadBuilding
      ? clickedSuggestion.id
      : null;

  // Reset form when default values change
  useEffect(() => {
    formMethods.reset(memoizedDefaultValues, {
      keepDefaultValues: false,
      keepTouched: false,
      keepDirtyValues: false,
      keepDirty: false,
    });

    return scheduleLoadBuildingSuggestionValidation(
      createModeLoadBuildingSuggestionId != null && !builtLoad,
      () => {
        void formMethods.trigger();
      }
    );
  }, [
    memoizedDefaultValues,
    createModeLoadBuildingSuggestionId,
    builtLoad,
    formMethods,
  ]);

  // After reset: apply CI/Appt via dirty setValue (do not merge into defaults)
  useApplyLoadViewSuggestion(
    formMethods.setValue,
    clickedSuggestion,
    load?.freightTrackingID,
    !isCreateMode,
    memoizedDefaultValues
  );

  useFocusLoadViewSuggestion(
    clickedSuggestion,
    load?.freightTrackingID,
    setActiveTabs,
    !isCreateMode,
    Object.values(AvailableTabs)
  );

  // Inject the selected customer into the customers array so select component can properly display it
  useEffect(() => {
    if (!isLoadingCustomers && defaultCustomer?.externalTMSID) {
      setCustomers((prevCustomers) =>
        injectSelectedObject(defaultCustomer, prevCustomers ?? [])
      );
    }
  }, [defaultCustomer, isLoadingCustomers]);
  useEffect(() => {
    if (
      !isLoadingCustomers &&
      shouldPreserveDistinctBillToFromSuggestion &&
      defaultBillTo?.externalTMSID
    ) {
      const billToAsCustomerOption: TMSCustomer = {
        ...defaultBillTo,
        refNumber: '',
      };
      setCustomers((prevCustomers) =>
        injectSelectedObject(billToAsCustomerOption, prevCustomers ?? [])
      );
    }
  }, [
    defaultBillTo,
    isLoadingCustomers,
    shouldPreserveDistinctBillToFromSuggestion,
  ]);

  useEnsureTmsCustomerRowForExternalId({
    externalTmsId: watchedCustomerExternalTMSID,
    customers,
    setCustomers,
    tmsID,
    isLoadingCustomers,
    searchCustomersNonDestructive,
    resetToken: clickedSuggestion?.id,
  });

  useEnsureTmsCustomerRowForExternalId({
    externalTmsId: watchedBillToExternalTMSID,
    customers,
    setCustomers,
    tmsID,
    isLoadingCustomers,
    searchCustomersNonDestructive,
    resetToken: clickedSuggestion?.id,
  });

  // Inject the selected locations from AI suggestions into the locations array.
  // Depends on `hasFetchedLocations` rather than `locations` so the functional
  // update below cannot retrigger this effect.
  const hasFetchedLocations = locations != null;
  useEffect(() => {
    if (isLoadingLocations || !hasFetchedLocations) {
      return;
    }

    const additionalLocations: TMSLocation[] = [];
    if (defaultPickup?.externalTMSID) {
      additionalLocations.push(defaultPickup);
    }
    if (defaultConsignee?.externalTMSID) {
      additionalLocations.push(defaultConsignee);
    }

    if (additionalLocations.length > 0) {
      setLocations((prevLocations) => {
        let updatedLocations = prevLocations ?? [];
        additionalLocations.forEach((loc) => {
          updatedLocations = injectSelectedObject(loc, updatedLocations);
        });
        return updatedLocations;
      });
    }
  }, [
    isLoadingLocations,
    hasFetchedLocations,
    defaultPickup,
    defaultConsignee,
  ]);

  // Hook to cache load building form
  useEffect(() => {
    if (isCreateMode) {
      const subscription = formMethods.watch((value) => {
        FormStorageService.saveFormState(`${tmsName}_${threadId}`, {
          threadID: threadId,
          values: value,
          clickedSuggestion: isLoadBuildingSuggestionClicked
            ? clickedSuggestion
            : null,
          dirtyFields: formMethods.formState.dirtyFields,
        });
      });
      return () => subscription.unsubscribe();
    }
    return undefined;
  }, [
    formMethods,
    threadId,
    isLoadBuildingSuggestionClicked,
    clickedSuggestion,
    tmsName,
    isCreateMode,
  ]);

  function handleClearForm() {
    if (isCreateMode) {
      setShowAIHints(false);
      setBuiltLoad(null);
      setShouldPreserveDistinctBillToFromSuggestion(false);
      FormStorageService.clearFormState(`${tmsName}_${threadId}`);

      // Deselect the Kit suggestion
      setCurrentState((prevState) => ({
        ...prevState,
        clickedSuggestion: null,
      }));

      const emptyForm = initLoadBuildingForm(tmsName);
      formMethods.reset(emptyForm, {
        keepDefaultValues: false,
        keepDirty: false,
        keepDirtyValues: false,
        keepTouched: false,
      });

      // Clear any validation errors
      formMethods.clearErrors();
    }
  }

  const onInvalid: SubmitErrorHandler<NormalizedLoad> = (errors) => {
    handleFormErrors(errors, {
      tmsName: TMS.Aljex,
      maxLabelsToShow: DEFAULT_MAX_VALIDATION_TOAST_LABELS,
      activeTabs,
      setActiveTabs,
      toast,
    });
  };

  const onSubmitCreate: SubmitHandler<NormalizedLoad> = async (data) => {
    setIsLoading(true);
    data.freightTrackingID = '';
    data.externalTMSID = '';

    const form = getValues();

    const applySubmitValidationErrors = (
      fieldErrors: ManualFieldError<NormalizedLoad>[]
    ) =>
      applyManualValidationErrors(
        formMethods,
        fieldErrors,
        loadBuildingManualValidationDisplayOptions({
          tmsName: TMS.Aljex,
          activeTabs,
          setActiveTabs,
          toast,
          afterErrorHandled: () => setIsLoading(false),
        })
      );

    formMethods.clearErrors();

    const duplicateStops = validateDuplicateStopLocations(
      form.pickup?.externalTMSID,
      form.consignee?.externalTMSID
    );
    if (!duplicateStops.ok) {
      applySubmitValidationErrors([
        { path: 'pickup.externalTMSID', message: duplicateStops.message },
        { path: 'consignee.externalTMSID', message: duplicateStops.message },
      ]);
      return;
    }

    const pickupDeliveryDates = validateDeliveryOnOrAfterPickupDate(
      form.pickup?.readyTime,
      form.consignee?.mustDeliver
    );
    if (!pickupDeliveryDates.ok) {
      applySubmitValidationErrors([
        { path: 'consignee.mustDeliver', message: pickupDeliveryDates.message },
      ]);
      return;
    }

    // no more handling enum on the FE. BE does it already
    const commoditiesData = form.commodities || [];
    const additionalReferencesData = form.additionalReferences || [];

    const stops = form.stops || [];
    if (!stops || stops.length < 2) {
      toast({
        description:
          'Please provide at least a pickup and a dropoff (2 stops).',
        variant: 'destructive',
      });
      setIsLoading(false);
      return;
    }
    const firstStop = stops[0];
    const lastStop = stops[stops.length - 1];

    const stopsForApi: LoadStopsItem[] = stops.map((s) => {
      const mergedAddress = {
        name: s.address?.name ?? (s as any).name ?? '',
        addressLine1: s.address?.addressLine1 ?? (s as any).addressLine1 ?? '',
        addressLine2: s.address?.addressLine2 ?? (s as any).addressLine2 ?? '',
        city: s.address?.city ?? (s as any).city ?? '',
        state: s.address?.state ?? (s as any).state ?? '',
        zip: s.address?.zip ?? (s as any).zipCode ?? '',
        country: s.address?.country ?? (s as any).country ?? '',
        timezone: s.address?.timezone ?? (s as any).timezone ?? '',
        isVerified: s.address?.isVerified ?? false,
      };

      const {
        name: _name,
        addressLine1: _a1,
        addressLine2: _a2,
        city: _city,
        state: _state,
        zipCode: _zip,
        country: _country,
        ...rest
      } = s as any;

      const stopWithNested = { ...rest, address: mergedAddress };
      return denormalizeDatesForTMSForm(
        tmsName,
        stopWithNested
      ) as LoadStopsItem;
    });

    const { address: _pAddr, ...pickupRest } = firstStop as any;

    const pickupFlat = {
      ...pickupRest,
      name: firstStop?.address?.name ?? pickupRest.name ?? '',
      addressLine1:
        firstStop?.address?.addressLine1 ?? pickupRest.addressLine1 ?? '',
      addressLine2:
        firstStop?.address?.addressLine2 ?? pickupRest.addressLine2 ?? '',
      city: firstStop?.address?.city ?? pickupRest.city ?? '',
      state: firstStop?.address?.state ?? pickupRest.state ?? '',
      zipCode: firstStop?.address?.zip ?? pickupRest.zipCode ?? '',
      country: firstStop?.address?.country ?? pickupRest.country ?? '',
    };

    const pickup = denormalizeDatesForTMSForm(tmsName, pickupFlat) as Pickup;
    const pickupFormFields = form.pickup;

    if (pickupFormFields) {
      pickup.businessHours =
        pickupFormFields.businessHours ?? pickup.businessHours;

      pickup.refNumber = pickupFormFields.refNumber ?? pickup.refNumber;

      pickup.readyTime = pickupFormFields.readyTime
        ? new Date(pickupFormFields.readyTime).toISOString()
        : pickup.readyTime;

      pickup.apptStartTime = pickupFormFields.apptStartTime
        ? new Date(pickupFormFields.apptStartTime).toISOString()
        : pickup.apptStartTime;

      pickup.apptNote = pickupFormFields.apptNote ?? pickup.apptNote;

      stopsForApi[0].businessHours = pickup.businessHours;
      stopsForApi[0].refNumber = pickup.refNumber;
      stopsForApi[0].apptNote = pickup.apptNote;
      stopsForApi[0].readyTime = pickup.readyTime ?? '';
      stopsForApi[0].apptStartTime = pickup.apptStartTime ?? '';
    }

    const { address: _cAddr, ...consigneeRest } = lastStop as any;

    const consigneeFlat = {
      ...consigneeRest,
      name: lastStop?.address?.name ?? consigneeRest.name ?? '',
      addressLine1:
        lastStop?.address?.addressLine1 ?? consigneeRest.addressLine1 ?? '',
      addressLine2:
        lastStop?.address?.addressLine2 ?? consigneeRest.addressLine2 ?? '',
      city: lastStop?.address?.city ?? consigneeRest.city ?? '',
      state: lastStop?.address?.state ?? consigneeRest.state ?? '',
      zipCode: lastStop?.address?.zip ?? consigneeRest.zipCode ?? '',
      country: lastStop?.address?.country ?? consigneeRest.country ?? '',
    };

    const consignee = denormalizeDatesForTMSForm(
      tmsName,
      consigneeFlat
    ) as Dropoff;
    const consigneeFormFields = form.consignee;
    const lastStopIndex = stopsForApi.length - 1;

    if (consigneeFormFields) {
      consignee.businessHours =
        consigneeFormFields.businessHours ?? consignee.businessHours;

      consignee.refNumber =
        consigneeFormFields.refNumber ?? consignee.refNumber;

      consignee.mustDeliver = consigneeFormFields.mustDeliver
        ? new Date(consigneeFormFields.mustDeliver).toISOString()
        : consignee.mustDeliver;

      consignee.apptStartTime = consigneeFormFields.apptStartTime
        ? new Date(consigneeFormFields.apptStartTime).toISOString()
        : consignee.apptStartTime;

      consignee.apptNote = consigneeFormFields.apptNote ?? consignee.apptNote;

      stopsForApi[lastStopIndex].businessHours = consignee.businessHours;
      stopsForApi[lastStopIndex].refNumber = consignee.refNumber;
      stopsForApi[lastStopIndex].apptNote = consignee.apptNote;
      stopsForApi[lastStopIndex].mustDeliver = consignee.mustDeliver ?? '';
      stopsForApi[lastStopIndex].apptStartTime = consignee.apptStartTime ?? '';
    }

    const mappedRateData: RateData = {
      ...form.rateData,
      customerRateType: form.rateData?.customerRateType
        ? mapAljexRateTypeToCode(
            normalizeAljexRateType(form.rateData.customerRateType)
          )
        : '',
      carrierRateType: form.rateData?.carrierRateType
        ? mapAljexRateTypeToCode(
            normalizeAljexRateType(form.rateData.carrierRateType)
          )
        : '',
      customerLineItems: withUpdatedFuelItem(
        form.rateData?.customerLineItems || [],
        getFuelFlatDisplayValue(form.rateData?.customerLineItems)
      ),
      carrierLineItems: withUpdatedFuelItem(
        form.rateData?.carrierLineItems || [],
        getFuelFlatDisplayValue(form.rateData?.carrierLineItems)
      ),
    };

    const notesArray: Note[] = [];

    if (pickup?.apptNote?.trim()) {
      notesArray.push({
        note: `${pickup.apptNote.trim()}`,
        updatedBy: '',
        createdAt: new Date().toISOString(),
        isException: null,
        isOnTime: null,
        source: 'form',
      });
    }

    const reqData = withLoadBuildingSuggestionContext(
      { clickedSuggestion, threadId, emailId, emailExternalId: threadItemId },
      {
        load: {
          ...form,
          tmsID: tmsID ?? 0,
          operator: form.operator,
          mode: form.mode,
          carrier: form.carrier as LoadCarrier,
          customer: denormalizeDatesForTMSForm(
            tmsName,
            form.customer
          ) as Customer,
          billTo: denormalizeDatesForTMSForm(
            tmsName,
            form.billTo
          ) as CompanyCoreInfo,
          specifications: {
            ...form.specifications,
          },
          // Stops should have a nested address.
          stops: stopsForApi,
          moreThanTwoStops: stopsForApi.length > 2,
          // Pickup/Consignee should have flat address fields.
          pickup: pickup,
          consignee: consignee,
          rateData: mappedRateData,
          commodities: commoditiesData,
          notes: notesArray,
          additionalReferences: additionalReferencesData
            .filter((ref) => ref.number && ref.number.trim())
            .map((ref) => ({
              qualifier: ref.qualifier || '',
              number: ref.number,
              weight: ref.weight || 0,
              pieces: ref.pieces || 0,
              shouldSendToDriver: ref.shouldSendToDriver || false,
            })),
        },
      }
    );

    const res = await guardedCreateLoad(data.customer, reqData, customers);
    if (!res) {
      setIsLoading(false);
      return;
    }

    if (res.isOk()) {
      setBuiltLoad(res.value.load);
      FormStorageService.clearFormState(`${tmsName}_${threadId}`);
      if (
        clickedSuggestion &&
        clickedSuggestion.pipeline === SuggestionPipelines.LoadBuilding
      ) {
        setCurrentState((prevState) => ({
          ...prevState,
          ...applyCompleteKitSuggestions(
            prevState,
            clickedSuggestion.id,
            SuggestionPipelines.LoadBuilding
          ),
        }));
      }
      onSuccess?.(res.value);
    } else {
      toast({
        description: res.error.message || 'Failed to create load.',
        variant: 'destructive',
      });
      onError?.(res.error);
    }
    setIsLoading(false);
  };

  const onSubmitEdit: SubmitHandler<NormalizedLoad> = async () => {
    if (!load) {
      return;
    }

    setIsLoading(true);

    const form = getValues();

    const applySubmitValidationErrors = (
      fieldErrors: ManualFieldError<NormalizedLoad>[]
    ) =>
      applyManualValidationErrors(
        formMethods,
        fieldErrors,
        loadBuildingManualValidationDisplayOptions({
          tmsName: TMS.Aljex,
          activeTabs,
          setActiveTabs,
          toast,
          afterErrorHandled: () => setIsLoading(false),
        })
      );

    formMethods.clearErrors();

    const duplicateStops = validateDuplicateStopLocations(
      form.pickup?.externalTMSID,
      form.consignee?.externalTMSID
    );
    if (!duplicateStops.ok) {
      applySubmitValidationErrors([
        { path: 'pickup.externalTMSID', message: duplicateStops.message },
        { path: 'consignee.externalTMSID', message: duplicateStops.message },
      ]);
      return;
    }

    const pickupDeliveryDates = validateDeliveryOnOrAfterPickupDate(
      form.pickup?.readyTime,
      form.consignee?.mustDeliver
    );
    if (!pickupDeliveryDates.ok) {
      applySubmitValidationErrors([
        { path: 'consignee.mustDeliver', message: pickupDeliveryDates.message },
      ]);
      return;
    }

    const commoditiesData = form.commodities || [];
    const additionalReferencesData = form.additionalReferences || [];

    const stops = form.stops || [];
    if (!stops || stops.length < 2) {
      toast({
        description:
          'Please provide at least a pickup and a dropoff (2 stops).',
        variant: 'destructive',
      });
      setIsLoading(false);
      return;
    }
    const firstStop = stops[0];
    const lastStop = stops[stops.length - 1];

    const stopsForApi: LoadStopsItem[] = stops.map((s) => {
      const mergedAddress = {
        name: s.address?.name ?? (s as any).name ?? '',
        addressLine1: s.address?.addressLine1 ?? (s as any).addressLine1 ?? '',
        addressLine2: s.address?.addressLine2 ?? (s as any).addressLine2 ?? '',
        city: s.address?.city ?? (s as any).city ?? '',
        state: s.address?.state ?? (s as any).state ?? '',
        zip: s.address?.zip ?? (s as any).zipCode ?? '',
        country: s.address?.country ?? (s as any).country ?? '',
        timezone: s.address?.timezone ?? (s as any).timezone ?? '',
        isVerified: s.address?.isVerified ?? false,
      };

      const {
        name: _name,
        addressLine1: _a1,
        addressLine2: _a2,
        city: _city,
        state: _state,
        zipCode: _zip,
        country: _country,
        ...rest
      } = s as any;

      const stopWithNested = { ...rest, address: mergedAddress };
      return denormalizeDatesForTMSForm(
        tmsName,
        stopWithNested
      ) as LoadStopsItem;
    });

    const { address: _pAddr, ...pickupRest } = firstStop as any;

    const pickupFlat = {
      ...pickupRest,
      name: firstStop?.address?.name ?? pickupRest.name ?? '',
      addressLine1:
        firstStop?.address?.addressLine1 ?? pickupRest.addressLine1 ?? '',
      addressLine2:
        firstStop?.address?.addressLine2 ?? pickupRest.addressLine2 ?? '',
      city: firstStop?.address?.city ?? pickupRest.city ?? '',
      state: firstStop?.address?.state ?? pickupRest.state ?? '',
      zipCode: firstStop?.address?.zip ?? pickupRest.zipCode ?? '',
      country: firstStop?.address?.country ?? pickupRest.country ?? '',
    };

    const pickup = {
      ...denormalizeDatesForTMSForm(tmsName, pickupFlat),
      externalTMSID: firstStop.externalTMSID,
    } as Pickup;
    const pickupFormFields = form.pickup;

    if (pickupFormFields) {
      pickup.businessHours =
        pickupFormFields.businessHours ?? pickup.businessHours;

      pickup.refNumber = pickupFormFields.refNumber ?? pickup.refNumber;

      pickup.readyTime = pickupFormFields.readyTime
        ? new Date(pickupFormFields.readyTime).toISOString()
        : pickup.readyTime;

      pickup.apptStartTime = pickupFormFields.apptStartTime
        ? new Date(pickupFormFields.apptStartTime).toISOString()
        : pickup.apptStartTime;

      pickup.apptNote = pickupFormFields.apptNote ?? pickup.apptNote;

      stopsForApi[0].businessHours = pickup.businessHours;
      stopsForApi[0].refNumber = pickup.refNumber;
      stopsForApi[0].apptNote = pickup.apptNote;
      stopsForApi[0].readyTime = pickup.readyTime ?? '';
      stopsForApi[0].apptStartTime = pickup.apptStartTime ?? '';
    }

    const { address: _cAddr, ...consigneeRest } = lastStop as any;

    const consigneeFlat = {
      ...consigneeRest,
      name: lastStop?.address?.name ?? consigneeRest.name ?? '',
      addressLine1:
        lastStop?.address?.addressLine1 ?? consigneeRest.addressLine1 ?? '',
      addressLine2:
        lastStop?.address?.addressLine2 ?? consigneeRest.addressLine2 ?? '',
      city: lastStop?.address?.city ?? consigneeRest.city ?? '',
      state: lastStop?.address?.state ?? consigneeRest.state ?? '',
      zipCode: lastStop?.address?.zip ?? consigneeRest.zipCode ?? '',
      country: lastStop?.address?.country ?? consigneeRest.country ?? '',
    };

    const consignee = {
      ...denormalizeDatesForTMSForm(tmsName, consigneeFlat),
      externalTMSID: lastStop.externalTMSID,
    } as Dropoff;
    const consigneeFormFields = form.consignee;
    const lastStopIndex = stopsForApi.length - 1;

    if (consigneeFormFields) {
      consignee.businessHours =
        consigneeFormFields.businessHours ?? consignee.businessHours;

      consignee.refNumber =
        consigneeFormFields.refNumber ?? consignee.refNumber;

      consignee.mustDeliver = consigneeFormFields.mustDeliver
        ? new Date(consigneeFormFields.mustDeliver).toISOString()
        : consignee.mustDeliver;

      consignee.apptStartTime = consigneeFormFields.apptStartTime
        ? new Date(consigneeFormFields.apptStartTime).toISOString()
        : consignee.apptStartTime;

      consignee.apptNote = consigneeFormFields.apptNote ?? consignee.apptNote;

      stopsForApi[lastStopIndex].businessHours = consignee.businessHours;
      stopsForApi[lastStopIndex].refNumber = consignee.refNumber;
      stopsForApi[lastStopIndex].apptNote = consignee.apptNote;
      stopsForApi[lastStopIndex].mustDeliver = consignee.mustDeliver ?? '';
      stopsForApi[lastStopIndex].apptStartTime = consignee.apptStartTime ?? '';
    }

    const notesArray: Note[] = [];

    if (pickup?.apptNote?.trim()) {
      notesArray.push({
        note: `${pickup.apptNote.trim()}`,
        updatedBy: '',
        createdAt: new Date().toISOString(),
        isException: null,
        isOnTime: null,
        source: 'form',
      });
    }

    // if (consignee?.apptNote?.trim()) {
    //   notesArray.push({
    //     note: `Delivery Instructions: ${consignee.apptNote.trim()}`,
    //   } as Note);
    // }

    const reqData = {
      operation: TMSOperation.UpdateLoad,
      load: {
        ...load,
        freightTrackingID: form.freightTrackingID,
        externalTMSID: form.externalTMSID,
        operator: form.operator,
        customer: denormalizeDatesForTMSForm(
          tmsName,
          form.customer
        ) as Customer,
        billTo: denormalizeDatesForTMSForm(
          tmsName,
          form.billTo
        ) as CompanyCoreInfo,
        specifications: denormalizeDatesForTMSForm(
          tmsName,
          form.specifications
        ),
        // Stops should have a nested address.
        stops: stopsForApi,
        moreThanTwoStops: stopsForApi.length > 2,
        // Pickup/Consignee should have flat address fields.
        pickup: pickup,
        consignee: consignee,
        carrier: {
          ...denormalizeDatesForTMSForm(tmsName, form.carrier),
          rateConfirmationSent: form.carrier.rateConfirmationSent
            ? true
            : false,
        } as LoadCarrier,
        rateData: {
          ...denormalizeDatesForTMSForm(
            tmsName,
            (() => {
              const {
                customerLineHaulCharge: _customerLineHaulCharge,
                customerTotalCharge: _customerTotalCharge,
                carrierLineHaulCharge: _carrierLineHaulCharge,
                ...cleanRateData
              } = form.rateData || {};
              return cleanRateData;
            })()
          ),

          customerRateType: form.rateData?.customerRateType
            ? mapAljexRateTypeToCode(
                normalizeAljexRateType(form.rateData.customerRateType)
              )
            : '',
          customerLineHaulRate: form.rateData?.customerLineHaulRate || null,
          customerRateNumUnits: form.rateData?.customerRateNumUnits || null,

          carrierRateType: form.rateData?.carrierRateType
            ? mapAljexRateTypeToCode(
                normalizeAljexRateType(form.rateData.carrierRateType)
              )
            : '',
          carrierLineHaulRate: form.rateData?.carrierLineHaulRate || null,
          carrierRateNumUnits: form.rateData?.carrierRateNumUnits || null,
          carrierMaxRate: form.rateData?.carrierMaxRate || null,

          salesperson1: form.rateData?.salesperson1 || '',
          salesperson1Percent: form.rateData?.salesperson1Percent || 0,
          collectionMethod: form.rateData?.collectionMethod || '',
          revenueCode: form.rateData?.revenueCode || '',
          customerLineHaulCharge: null,
          customerLineHaulUnit: form.rateData?.customerLineHaulUnit || '',
          customerTotalCharge: null,
          customerLineItems: withUpdatedFuelItem(
            form.rateData?.customerLineItems || [],
            getFuelFlatDisplayValue(form.rateData?.customerLineItems)
          ),
          carrierLineHaulCharge: null,
          carrierLineHaulUnit: form.rateData?.carrierLineHaulUnit || '',
          carrierTotalCost: null,
          carrierLineItems: withUpdatedFuelItem(
            form.rateData?.carrierLineItems || [],
            getFuelFlatDisplayValue(form.rateData?.carrierLineItems)
          ),
          fscPercent: form.rateData?.fscPercent || null,
          fscPerMile: form.rateData?.fscPerMile || null,
          fscFlatRate: form.rateData?.fscFlatRate || null,
          netProfitUSD: form.rateData?.netProfitUSD || null,
          profitPercent: form.rateData?.profitPercent || null,
        },
        commodities: commoditiesData
          .filter((item) => item.description.trim())
          .map((item) => ({
            description: item.description,
            quantity: item.quantity || 0,
            packagingType: 'Pallets',
            weightTotal: item.weightTotal || 0,
            length: item.length || 0,
            width: item.width || 0,
            height: item.height || 0,
            freightClass: item.freightClass || '',
            referenceNumber: item.referenceNumber || '',
            handlingQuantity: 0,
            dimensionUnit: '',
            hazardousMaterial: false,
            nmfc: '',
            additionalMarkings: '',
            unNumber: '',
            packagingGroup: '',
            hazmatCustomClassDescription: '',
            hazmatPieceDescription: '',
            harmonizedCode: '',
            hazardClasses: null,
            loadID: 0,
          })),
        notes: notesArray,
        additionalReferences: additionalReferencesData
          .filter((ref) => ref.number && ref.number.trim())
          .map((ref) => ({
            qualifier: ref.qualifier || '',
            number: ref.number,
            weight: ref.weight || 0,
            pieces: ref.pieces || 0,
            shouldSendToDriver: ref.shouldSendToDriver || false,
          })),
      },
    };

    const res = await updateTMS(load.ID!, reqData);

    if (res.isOk()) {
      toast({
        description: res.value.message,
        variant: 'success',
      });
      onSuccess?.(res.value);
      await acceptLoadViewSuggestion(
        clickedSuggestion,
        reqData.load,
        setCurrentState,
        undefined,
        tmsName
      );
    } else {
      toast({
        description: res.error.message,
        variant: 'destructive',
      });
      onError?.(res.error);
    }

    setIsLoading(false);
  };

  const handleFormSubmit: SubmitHandler<NormalizedLoad> = async (data) => {
    if (onSubmit) {
      onSubmit(data);
    } else if (isCreateMode) {
      await onSubmitCreate(data);
    } else {
      await onSubmitEdit(data);
    }
  };

  const buttonText = isCreateMode ? ButtonText.BuildLoad : ButtonText.UpdateTMS;
  const buttonNamePosthog = isCreateMode
    ? ButtonNamePosthog.BuildLoad
    : ButtonNamePosthog.UpdateTMS;

  return (
    <Flex direction='col' className='mb-5 w-full'>
      <ExtendedFormProvider
        aiDefaultValues={showAIHints}
        aiIconOnly={true}
        highlightDirtyFields={!isCreateMode}
      >
        <FormProvider {...formMethods}>
          <form
            onSubmit={handleSubmit(handleFormSubmit, onInvalid)}
            className='w-full'
          >
            <LoadBuildingAccordionControls
              activeTabs={activeTabs}
              allTabs={allTabs}
              sectionTabs={sectionTabs}
              onExpandAll={expandAll}
              onCollapseAll={collapseAll}
              defaultView={defaultView}
              onDefaultViewChange={setDefaultView}
            />
            <Accordion
              type='multiple'
              value={activeTabs}
              onValueChange={handleAccordionValueChange}
              className='w-full'
            >
              <LoadSectionAccordionItem
                label='Customer'
                icon={<Building2 className='h-6 w-6' strokeWidth={1} />}
                name={AvailableTabs.customer}
                activeTabs={activeTabs}
              >
                <CustomerSectionForm
                  formMethods={formMethods}
                  customers={customers}
                  setCustomers={setCustomers}
                  isLoadingCustomers={isLoadingCustomers}
                  setIsLoadingCustomers={setIsLoadingCustomers}
                  originalSuggestionData={
                    clickedSuggestion?.suggested as NormalizedLoad
                  }
                  isCreateMode={isCreateMode}
                />
              </LoadSectionAccordionItem>

              <LoadSectionAccordionItem
                label='Bill To'
                icon={<ReceiptIcon className='h-6 w-6' strokeWidth={1} />}
                name={AvailableTabs.billTo}
                activeTabs={activeTabs}
              >
                <BillToSectionForm
                  formMethods={formMethods}
                  customers={customers}
                  setCustomers={setCustomers}
                  isLoadingCustomers={isLoadingCustomers}
                  setIsLoadingCustomers={setIsLoadingCustomers}
                  isCreateMode={isCreateMode}
                  preserveDistinctBillToFromSuggestion={
                    shouldPreserveDistinctBillToFromSuggestion
                  }
                />
              </LoadSectionAccordionItem>

              <LoadSectionAccordionItem
                label={isCreateMode ? 'Commodities' : 'Specs'}
                icon={<PackageIcon className='h-6 w-6' strokeWidth={1} />}
                name={AvailableTabs.commodities}
                activeTabs={activeTabs}
              >
                <SpecificationsForm
                  isCreateMode={isCreateMode}
                  formMethods={formMethods}
                  initialTransportType={
                    memoizedDefaultValues?.specifications?.transportType
                  }
                />
              </LoadSectionAccordionItem>

              <LoadSectionAccordionItem
                label='Rates'
                icon={
                  <CircleDollarSignIcon className='h-6 w-6' strokeWidth={1} />
                }
                name={AvailableTabs.rates}
                activeTabs={activeTabs}
              >
                <RatesForm
                  formMethods={formMethods}
                  isCreateMode={isCreateMode}
                />
              </LoadSectionAccordionItem>

              <LoadSectionAccordionItem
                label='Route'
                icon={<MapPinnedIcon className='h-6 w-6' strokeWidth={1} />}
                name={AvailableTabs.route}
                activeTabs={activeTabs}
              >
                <LoadSectionAccordionItem
                  label='Pickup'
                  icon={<BoxIcon className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.pickup}
                  activeTabs={activeTabs}
                  labelAccessory={<AljexStopSectionInfoTooltip />}
                >
                  <StopForm
                    stopKey='stops.0'
                    stopType={LoadStopsItemTypes.Pickup}
                    formMethods={formMethods}
                    isLoadingLocations={isLoadingLocations}
                    locations={locations}
                    handleRefreshLocations={handleRefreshLocations}
                    setLocations={setLocations}
                    originalSuggestionData={
                      clickedSuggestion?.suggested as NormalizedLoad
                    }
                    isCreateMode={isCreateMode}
                    tmsID={tmsID!}
                  />
                </LoadSectionAccordionItem>

                {/* Intermediate stops */}
                {fields.slice(1, -1).map((field, index) => (
                  <div
                    key={field.id}
                    className='grid gap-1.5 grid-cols-1 w-full mx-0'
                  >
                    <div className='col-span-3'>
                      <LoadSectionAccordionItem
                        label={`Stop ${index + 1}`}
                        icon={
                          <MapPinIcon className='h-6 w-6' strokeWidth={1} />
                        }
                        name={`stop-${index}`}
                        activeTabs={activeTabs}
                      >
                        <StopForm
                          stopKey={`stops.${index + 1}`}
                          stopType={LoadStopsItemTypes.Stop}
                          formMethods={formMethods}
                          isLoadingLocations={isLoadingLocations}
                          locations={locations}
                          handleRefreshLocations={handleRefreshLocations}
                          setLocations={setLocations}
                          originalSuggestionData={
                            clickedSuggestion?.suggested as NormalizedLoad
                          }
                          isCreateMode={isCreateMode}
                          tmsID={tmsID!}
                        />
                      </LoadSectionAccordionItem>
                    </div>
                  </div>
                ))}

                <LoadSectionAccordionItem
                  label='Consignee'
                  icon={<WarehouseIcon className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.consignee}
                  activeTabs={activeTabs}
                  labelAccessory={<AljexStopSectionInfoTooltip />}
                >
                  <StopForm
                    stopKey={`stops.${fields.length - 1}`}
                    stopType={LoadStopsItemTypes.Dropoff}
                    formMethods={formMethods}
                    isLoadingLocations={isLoadingLocations}
                    locations={locations}
                    handleRefreshLocations={handleRefreshLocations}
                    setLocations={setLocations}
                    originalSuggestionData={
                      clickedSuggestion?.suggested as NormalizedLoad
                    }
                    isCreateMode={isCreateMode}
                    tmsID={tmsID!}
                  />
                </LoadSectionAccordionItem>
              </LoadSectionAccordionItem>

              {!isCreateMode && (
                <LoadSectionAccordionItem
                  label='Carrier'
                  icon={<TruckIcon className='h-6 w-6' strokeWidth={1} />}
                  name={AvailableTabs.carrier}
                  activeTabs={activeTabs}
                >
                  <CarrierSectionForm formMethods={formMethods} />
                </LoadSectionAccordionItem>
              )}

              <LoadSectionAccordionItem
                label='Additional Info'
                icon={<Weight className='h-6 w-6' strokeWidth={1} />}
                name={AvailableTabs.additionalReferences}
                activeTabs={activeTabs}
              >
                <AdditionalReferencesForm
                  formMethods={formMethods}
                  isCreateMode={isCreateMode}
                />
              </LoadSectionAccordionItem>
            </Accordion>

            <Flex direction='col' className='w-full mt-4'>
              <Button
                buttonNamePosthog={buttonNamePosthog}
                type='submit'
                className='w-full'
                disabled={isLoading}
                data-testid={
                  isCreateMode
                    ? 'create-load-submit'
                    : 'load-info-update-submit'
                }
                logProperties={
                  !isCreateMode && load
                    ? {
                        loadID: load.ID,
                        freightTrackingID: load.freightTrackingID,
                        serviceID: load.serviceID,
                      }
                    : undefined
                }
              >
                {isLoading ? <ButtonLoader /> : buttonText}
              </Button>

              {isCreateMode && formMethods.formState.isDirty && (
                <Flex
                  direction='row'
                  justify='center'
                  align='center'
                  className='w-full'
                >
                  <Button
                    buttonNamePosthog={ButtonNamePosthog.ClearForm}
                    type='button'
                    className='mt-4 h-8 text-sm text-neutral-700'
                    disabled={isLoading}
                    variant='outline'
                    onClick={handleClearForm}
                  >
                    {ButtonText.ClearForm}
                  </Button>
                </Flex>
              )}

              {isCreateMode && builtLoad?.externalTMSID && (
                <LoadCreatedMessage builtLoadId={builtLoad.externalTMSID} />
              )}
            </Flex>
          </form>
        </FormProvider>
      </ExtendedFormProvider>
    </Flex>
  );
}
